import { zodResolver } from "@hookform/resolvers/zod";
import { FieldPath, useForm } from "react-hook-form";
import FormFieldInput from "@/src/components/FormField/FormFieldInput";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import FormSection from "@/src/components/FormSection/FormSection";
import { CreateTenantFormValuesType, createTenantFormSchema } from "../schemas";
import CheckboxItem from "../../CheckboxItem/CheckboxItem";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import FormFieldSelect from "../../FormField/FormFieldSelect";
import useUldk from "@/src/hooks/useUldk";
import { tenantSlug } from "@/src/lib/tenantSlug";
import { formatPostalCode } from "@/src/lib/postalCode";

type TenantFormPropsType = {
  onSubmit: (values: CreateTenantFormValuesType) => void;
  defaultValues?: CreateTenantFormValuesType;
};

const CreateTenantForm = forwardRef(({ onSubmit, defaultValues }: TenantFormPropsType, ref) => {
  const navigate = useNavigate();
  const [saveAndCreateNew, setSaveAndCreateNew] = useState(false);
  const handleSaveCheckobxChange = (checked: boolean) => {
    setSaveAndCreateNew(checked);
  };
  const { voivodeships, districts, fetchDistricts, fetchTenants, tenants, fetchPrecincts } = useUldk({
    voivodeship: "24",
    district: "18",
  });
  useImperativeHandle(ref, () => ({
    onSuccessCreate() {
      saveAndCreateNew ? form.reset() : navigate({ to: "/admin/tenant" });
    },
  }));
  const form = useForm<CreateTenantFormValuesType>({
    resolver: zodResolver(createTenantFormSchema),
    defaultValues: defaultValues || {
      country: "Polska",
    },
  });

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    form.setValue("displayName", displayName, { shouldValidate: true, shouldDirty: true });
    form.setValue("id", tenantSlug(displayName), { shouldValidate: true, shouldDirty: true });
  };

  const handlePostalCodeChange =
    (fieldName: FieldPath<CreateTenantFormValuesType>) => (e: React.ChangeEvent<HTMLInputElement>) => {
      form.setValue(fieldName, formatPostalCode(e.target.value), {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormSection title="Dodaj gminę">
          <FormFieldInput
            form={form}
            description="Wyświetlana nazwa gminy (ID zostanie wygenerowane automatycznie)"
            placeholder="np. Gmina Przykładowa"
            label="Nazwa gminy"
            name="displayName"
            onChange={handleDisplayNameChange}
          />
          {form.watch("id") && (
            <p className="text-sm text-gray-500 -mt-2 mb-2">
              Wygenerowane ID: <strong>{form.watch("id")}</strong>
            </p>
          )}
          <FormFieldInput
            form={form}
            description="Na podany adres administrator dostanie wiadomość z linkiem aktywacyjnym do utworzenia hasła."
            placeholder="E-mail administratora"
            label="E-mail administratora"
            name="adminEmail"
          />
          <FormSection title="Dane kontaktowe" isCollapsible defaultOpen>
            <FormFieldInput form={form} description="" placeholder="Polska" label="Kraj" name="country" />
            <FormFieldSelect
              form={form}
              description=""
              placeholder="Województwo"
              label="Województwo"
              name="voivodeship"
              options={voivodeships}
              onChange={fetchDistricts}
            />
            <FormFieldSelect
              form={form}
              description=""
              placeholder="Powiat"
              label="Powiat"
              name="district"
              options={districts}
              onChange={fetchTenants}
            />
            <FormFieldSelect
              form={form}
              description=""
              placeholder="Gmina"
              label="Gmina"
              name="tenant"
              options={tenants}
              onChange={fetchPrecincts}
            />
            <div className="flex gap-2">
              <FormFieldInput
                form={form}
                description=""
                placeholder="Ulica"
                label="Ulica"
                name="street"
                className="w-1/2"
              />
              <FormFieldInput
                form={form}
                description=""
                placeholder="Numer domu"
                label="Numer domu"
                name="houseNumber"
                className="w-1/4"
              />
              <FormFieldInput
                form={form}
                description=""
                placeholder="Numer mieszkania"
                label="Numer mieszkania"
                name="apartmentNumber"
                className="w-1/4"
              />
            </div>
            <FormFieldInput form={form} description="" placeholder="Miasto" label="Miasto" name="town" />
            <FormFieldInput
              form={form}
              description=""
              placeholder="Kod pocztowy"
              label="Kod pocztowy"
              name="postalCode"
              onChange={handlePostalCodeChange("postalCode")}
            />
          </FormSection>
        </FormSection>
        <div className="flex flex-row-reverse justify-end gap-4">
          <CheckboxItem
            label="Zapisz i dodaj następne"
            id="save-and-create-new"
            onChange={handleSaveCheckobxChange}
            checked={saveAndCreateNew}
          />
          <Button type="submit">Zapisz</Button>
        </div>
      </form>
    </Form>
  );
});

export default CreateTenantForm;
